import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type {
	FoldableType,
	MenuItem,
	UniversalCreateMenuSection,
} from '@atlassian/universal-create/types';

import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useCanCreateSpace } from '@confluence/create-space/entry-points/createSpace.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { fg } from '@confluence/feature-gating';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useCfind5291TemplateTouchpoint } from '@confluence/experiment-cfind-5291-template-touchpoint/entry-points/useCfind5291TemplateTouchpoint.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { useSpaceEnabledContentTypes } from '../../CreateContentPopover/useSpaceEnabledContentTypes.tsx';
import { useIsCreateMenuLoomRecorderEntryPointEnabled } from '../../CreateContentPopover/CreateContentPopoverItems/index.ts';
import { useCwrSplitButtonExperiment } from '../cwrExperiment/useCwrSplitButtonExperiment.ts';

import { useLiveDocItem } from './useLiveDocItem.tsx';
import { usePageItem } from './usePageItem.tsx';
import { useWhiteboardItem } from './whiteboard/useWhiteboardItem.tsx';
import { useDatabaseItem } from './database/useDatabaseItem.tsx';
import { useBlogItem } from './useBlogItem.tsx';
import { useFolderItem } from './useFolderItem.tsx';
import { useEmbedItem } from './smart-link-embed/useEmbedItem.tsx';
import { useSpaceItem } from './useSpaceItem.tsx';
import { useLoomItem } from './useLoomItem.tsx';
import { useCreateAgentItem } from './useCreateAgentItem.tsx';
import { useAISmartCreateItem } from './useAISmartCreateItem.tsx';
import { useSlideItem } from './useSlideItem.tsx';
import { useStartFromTemplateItem } from './useStartFromTemplateItem.tsx';

export const ANALYTICS_EVENT_SOURCE = 'globalCreateContentPopover';
export const OVERFLOW_THRESHOLD_TEMPLATE_TOUCHPOINT: FoldableType = 5;

const messages = defineMessages({
	moreFromAtlassian: {
		id: 'create-blank-fabric-page.universal-create.suggestions',
		defaultMessage: 'Suggestions',
		description:
			'Section heading in the universal create menu that groups suggested content creation options recommended to the user.',
	},
});

export type UseMenuItemProps = {
	parentId?: string;
	source: string;
	spaceKey?: string;
	onClick: (actionSubjectId: string, attributes?: Record<string, string>) => void;
	flags?: FlagsStateContainer;
	openFrom?: string;
	isDisabled?: boolean;
};

export type UseMenuItemReturnProps = MenuItem;

/**
 * All sections this hook emits are app-defined. The platform union also
 * includes `SuggestionMenuSection` (no `items`), which we never produce —
 * narrowing here keeps consumers (and tests) free from union narrowing.
 */
export type AppDefinedMenuSection = Extract<
	UniversalCreateMenuSection,
	{ type: 'appDefinedSection' }
>;

const toAppDefinedSection = (
	section: Omit<AppDefinedMenuSection, 'type'>,
): AppDefinedMenuSection => ({ type: 'appDefinedSection', ...section });

/**
 * `parentIdOverride` lets callers pin the parent for menu-item creation to a
 * location other than the currently-viewed page — for example, when the user
 * is inside a managed folder tree and new items must land as siblings of the
 * managed folder root. Pass `null` to force creation at the space root, or
 * omit it to fall back to the current page's contentId.
 */
export function useMenuItems(
	flags: FlagsStateContainer,
	closeMenu?: () => void,
	openFrom?: string,
	isMenuOpen?: boolean,
	parentIdOverride?: string | null,
): AppDefinedMenuSection[] {
	const [spaceKey] = usePageSpaceKey();
	const [pageContentId] = usePageContentId();
	const contentId =
		parentIdOverride === undefined ? pageContentId : (parentIdOverride ?? undefined);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const routeDataRef = useRouteDataRef();
	const isOnEditRoute = useIsEditorPage();
	const isLivePage = useIsCurrentPageLive();
	const { isRovoLLMEnabled, accessStatus, isLoggedIn } = useSessionData();

	const { cohort: cwrSplitButtonCohort, isEnabled: splitButtonExperimentEnabled } =
		useCwrSplitButtonExperiment();

	const handleAnalyticsAndResetStickySearch = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					source: ANALYTICS_EVENT_SOURCE,
					attributes: {
						navdexPointType: END,
						isOnEditRoute,
						currentRouteName: routeDataRef?.current?.routeName,
						currentRouteIsLive: isLivePage,
						openFrom,
						...attributes,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute, routeDataRef, isLivePage, openFrom],
	);

	const onClick = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			handleAnalyticsAndResetStickySearch(actionSubjectId, attributes);
			closeMenu?.();
		},
		[handleAnalyticsAndResetStickySearch, closeMenu],
	);

	const { canCreateSpace } = useCanCreateSpace();
	const isLoomEntryPointEnabled = useIsCreateMenuLoomRecorderEntryPointEnabled();
	const {
		isCreateWhiteboardEnabled,
		isCreateBlogEnabled,
		isCreateLiveDocEnabled,
		isCreateDatabaseEnabled,
		isCreateFolderEnabled,
		isCreateSmartLinkEnabled,
	} = useSpaceEnabledContentTypes(spaceKey);

	const { isEnabled: isTemplateTouchpointEnabled } = useCfind5291TemplateTouchpoint({
		skip: !isMenuOpen,
	});

	const startFromTemplate = useStartFromTemplateItem({
		source: 'globalCreateDropdown-startFromTemplate',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;
	const isAifcEnabled = isRovoLLMEnabled && !isAnonymousUser;

	const smartCreate = useAISmartCreateItem({
		source: 'globalCreateDropdown-smartCreate',
		spaceKey,
		onClick,
		openFrom,
		isDisabled: !isAifcEnabled,
	});

	const slide = useSlideItem({
		source: 'globalCreateDropdown-slide',
		spaceKey,
		onClick,
	});

	const liveDoc = useLiveDocItem({
		source: 'globalCreateDropdown-liveDoc',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const page = usePageItem({
		source: 'globalCreateDropdown-page',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const whiteboard = useWhiteboardItem({
		source: 'globalCreateDropdown-whiteboard',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const database = useDatabaseItem({
		source: 'globalCreateDropdown-database',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const folder = useFolderItem({
		source: 'globalCreateDropdown-folder',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const blog = useBlogItem({
		source: 'globalCreateDropdown-blog',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const embed = useEmbedItem({
		source: 'globalCreateDropdown-smartLinkEmbed',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const loom = useLoomItem({
		source: 'globalCreateDropdown-smartLinkEmbed',
		parentId: contentId,
		spaceKey,
		onClick,
		flags,
	});

	const [agentItem, shouldRenderAgentItem] = useCreateAgentItem({
		parentId: contentId,
		spaceKey,
		onClick,
		isMenuOpen,
	});

	const space = useSpaceItem({
		source: ANALYTICS_EVENT_SOURCE,
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isOptedOutedOfWhiteboards = isWhiteboardFeatureEnabled('whiteboardOptOut');
	const isWhiteboardsEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');

	const whiteboardOptIn = !isOptedOutedOfWhiteboards && isWhiteboardsEnabled;

	const databaseOptIn =
		isContentTypeEnabledInCurrentEnv('database') && !fg('confluence_frontend_databases_opt-out');

	const slideOptIn =
		Boolean(isMenuOpen) || !fg('cc-maui-slides-vc90-improvement')
			? isContentTypeEnabledInCurrentEnv('slide')
			: false;

	const isSlideBaseEnabled = slideOptIn && isRovoLLMEnabled && !isAnonymousUser;

	const getSlideItem = () =>
		isSlideBaseEnabled &&
		expValEquals('confluence_frontend_cwr_outcome_type_picker', 'isEnabled', true)
			? [slide]
			: [];

	const getCommonItems = () => [
		...getSlideItem(),
		...(isCreateWhiteboardEnabled && whiteboardOptIn ? [whiteboard] : []),
		...(isCreateDatabaseEnabled && databaseOptIn ? [database] : []),
		...(shouldRenderAgentItem && expValEquals('cc_contextual_agent_creation_v2', 'isEnabled', true)
			? [agentItem]
			: []),
		...(isCreateFolderEnabled ? [folder] : []),
		...(isCreateBlogEnabled ? [blog] : []),
		...(isCreateSmartLinkEnabled ? [embed] : []),
	];

	const getRemainingItems = () => {
		return [
			...(isCreateLiveDocEnabled ? [liveDoc] : []),
			page,
			...getCommonItems(),
			...(canCreateSpace ? [space] : []),
		];
	};

	const getNativeItems = () => {
		return [
			...(isAifcEnabled && !splitButtonExperimentEnabled ? [smartCreate] : []),
			...(isCreateLiveDocEnabled ? [liveDoc] : []),
			page,
			...getCommonItems(),
			...(canCreateSpace ? [space] : []),
		];
	};

	const { formatMessage } = useIntl();

	const firstSectionItems = [
		...(isAifcEnabled && !splitButtonExperimentEnabled ? [smartCreate] : []),
		...(isTemplateTouchpointEnabled ? [startFromTemplate] : []),
	];

	const companionItems = [
		...(shouldRenderAgentItem && !expValEquals('cc_contextual_agent_creation_v2', 'isEnabled', true)
			? [agentItem]
			: []),
		...(isCreateSmartLinkEnabled && isLoomEntryPointEnabled ? [loom] : []),
	];

	// CWR split button experiment layout.
	// Smart create is removed from the menu (it's the primary split button action).
	// The contextual Agent creation treatment appends Agent after Database and
	// moves Folder after Agent. All enabled create options remain available:
	//   treatment: liveDoc, page, slide, whiteboard, database, agent, folder,
	//              space, embed, loom, blog
	// The existing CWR cohorts keep their current order when the treatment is off:
	//   variant_1 (foldable=5): liveDoc, page, slide, whiteboard, database (visible)
	//                           --- (folded) ---
	//                           folder, agent, space, embed, loom, blog
	//   variant_2 (foldable=6): liveDoc, page, slide, whiteboard, database, folder (visible)
	//                           --- (folded) ---
	//                           agent, space, embed, loom, blog
	if (splitButtonExperimentEnabled) {
		const isVariant2 = cwrSplitButtonCohort === 'variant_2';

		const cwrDropdownItems = [
			...(isCreateLiveDocEnabled ? [liveDoc] : []),
			page,
			...getSlideItem(),
			...(isCreateWhiteboardEnabled && whiteboardOptIn ? [whiteboard] : []),
			...(isCreateDatabaseEnabled && databaseOptIn ? [database] : []),
			...(shouldRenderAgentItem &&
			expValEquals('cc_contextual_agent_creation_v2', 'isEnabled', true)
				? [agentItem, ...(isCreateFolderEnabled ? [folder] : [])]
				: [
						...(isCreateFolderEnabled ? [folder] : []),
						...(shouldRenderAgentItem ? [agentItem] : []),
					]),
			...(canCreateSpace ? [space] : []),
			...(isCreateSmartLinkEnabled ? [embed] : []),
			...(isCreateSmartLinkEnabled && isLoomEntryPointEnabled ? [loom] : []),
			...(isCreateBlogEnabled ? [blog] : []),
		];

		// Keep overflow available regardless of Agent eligibility or its loading state.
		// variant_1 shows the first five items; variant_2 shows the first six.
		const cwrFoldableThreshold: FoldableType = isVariant2
			? 6
			: OVERFLOW_THRESHOLD_TEMPLATE_TOUCHPOINT;

		return [
			...(isTemplateTouchpointEnabled ? [toAppDefinedSection({ items: [startFromTemplate] })] : []),
			toAppDefinedSection({
				items: cwrDropdownItems,
				foldable: cwrFoldableThreshold,
				...(fg('navx_enable_overflow_evaluation_for_cwr')
					? { personalize: 'overflow' as const }
					: {}),
			}),
		];
	}

	if (isTemplateTouchpointEnabled) {
		return [
			...(firstSectionItems.length > 0 ? [toAppDefinedSection({ items: firstSectionItems })] : []),
			toAppDefinedSection({
				items: getRemainingItems(),
				foldable: OVERFLOW_THRESHOLD_TEMPLATE_TOUCHPOINT,
			}),
			...(companionItems.length > 0
				? [
						toAppDefinedSection({
							heading: formatMessage(messages.moreFromAtlassian),
							items: companionItems,
						}),
					]
				: []),
		];
	}

	return [
		toAppDefinedSection({ items: getNativeItems() }),
		...(companionItems.length > 0
			? [
					toAppDefinedSection({
						heading: formatMessage(messages.moreFromAtlassian),
						items: companionItems,
					}),
				]
			: []),
	];
}
